import { baseApi } from '@/Redux/baseApi';
import type { PricingUnit } from '@/lib/domain';
import { PRICING_LISTS, pricingListPath } from './pricingRoutes';

/**
 * `/pricing-lists` — a tenant's rate cards, and the rules that price them.
 *
 * Injected into the shared `baseApi` from beside the screen that owns it, the
 * same seam `Pages/Settings/guestSettingsApi.ts` uses: one cache, one tag
 * namespace, endpoints declared where they are read.
 *
 * ## Why one cache entry per LIST is the whole change
 *
 * The rules used to hang off a resource, so ten PlayStations meant ten cache
 * entries holding ten copies of the same four numbers — and a manager who put
 * the hourly rate up on one of them left the other nine quoting the old price
 * with no screen anywhere that could show the disagreement. A rate card is one
 * entry now. The floor map's ten cards read it once, and an edit to it repaints
 * all ten, because there is only one thing to repaint.
 *
 * ## The read is unfiltered, deliberately
 *
 * `?isActive=` exists and the editor does not send it. A deactivated card is
 * precisely what a manager chasing "where did Standard rates go?" has come to
 * find, and a list that quietly hid it would hide the answer. The *picker* that
 * attaches a card to a resource does filter — the API refuses to attach a
 * deactivated one, so offering it would be offering a 409.
 *
 * ## Why the writes are not mutations
 *
 * `baseApi`'s `axiosBaseQuery` reduces every failure to `{ status, message }` —
 * enough for a settings PATCH and not enough here. Every interesting refusal on
 * these routes travels as machine-readable keys beside the message (`code`,
 * `conflictsWithName`, `attachedResourceNames`, `ruleCount`), and a screen that
 * has to say WHICH rule a draft collides with cannot get that back out of an
 * English sentence — nor could it translate the sentence if it tried. So the
 * writes go through the axios client and are read by `lib/refusals.ts`; what
 * this module owns is the invalidation they report through.
 */

/** One rule of a rate card, exactly as the API sends it. */
export interface PricingRuleRow {
  id: string;
  name: string;
  unit: PricingUnit;
  /**
   * A decimal string, and one the API has already stripped: the column is
   * `Decimal(14,4)` and `Decimal.js`'s `toJSON` drops trailing zeros, so
   * `'25.5000'` arrives as `"25.5"` and `'40.0000'` as `"40"`. Never printed
   * raw — `formatCurrency` puts it back at the currency's own scale, because a
   * venue shown "SAR 25.5" is being shown a rate its own bills do not use.
   */
  price: string;
  durationMinutes: number | null;
  minMinutes: number | null;
  /** `0 = Sunday … 6 = Saturday`; empty means every day. */
  daysOfWeek: number[];
  startTime: string | null;
  endTime: string | null;
  priority: number;
  isActive: boolean;
}

/**
 * A rate card as a resource carries it — the three fields `GET /resources`
 * embeds, and no rules.
 *
 * Named rather than merely referenced by id, which is what makes a board of
 * consoles the place a venue notices that nine are on one card and the tenth is
 * on none. An id in a column cannot show that.
 */
export interface PricingListRef {
  id: string;
  name: string;
  isActive: boolean;
}

/** A row of `GET /pricing-lists`, with the two counts the index carries. */
export interface PricingListRow extends PricingListRef {
  description: string | null;
  _count?: { rules: number; resources: number };
}

/** `GET /pricing-lists/:id` — the card with the rules it prices from. */
export interface PricingListDetail extends PricingListRow {
  /**
   * Every rule, active and deactivated, highest priority first — the order the
   * engine reads them in, so the screen shows them in the order that decides
   * the price rather than in whatever order Postgres returned.
   */
  rules: PricingRuleRow[];
}

/** How many rules a card carries, whether the payload counted them or listed them. */
export function ruleCountOf(list: PricingListRow | PricingListDetail): number {
  if ('rules' in list && Array.isArray(list.rules)) return list.rules.length;
  return list._count?.rules ?? 0;
}

/** How many resources charge from this card. */
export function resourceCountOf(list: PricingListRow): number {
  return list._count?.resources ?? 0;
}

/**
 * A rate card changes when a manager sits down to change it, not during a shift
 * — but a shift is exactly when one gets edited, because a timer just refused to
 * start. Half an hour, so the editor, the floor map and the start dialog agree
 * without any of them re-reading on every open.
 */
const LISTS_TTL = 30 * 60;

export const pricingListsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * `GET /pricing-lists` — `resources.view`, and no scope check: a card
     * belongs to the tenant and has no place in the venue topology, so there is
     * no branch or area to compare a grant against.
     */
    pricingLists: build.query<PricingListRow[], void>({
      query: () => ({ url: PRICING_LISTS }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as PricingListRow[]) : [],
      keepUnusedDataFor: LISTS_TTL,
      providesTags: (result) => [
        { type: 'PricingList' as const, id: 'LIST' },
        ...(result ?? []).map((list) => ({ type: 'PricingList' as const, id: list.id })),
      ],
    }),

    /** `GET /pricing-lists/:id` — one card and its rules. */
    pricingList: build.query<PricingListDetail | null, string>({
      query: (listId) => ({ url: pricingListPath(listId) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as PricingListDetail) : null,
      keepUnusedDataFor: LISTS_TTL,
      providesTags: (_result, _error, listId) => [{ type: 'PricingList' as const, id: listId }],
    }),
  }),
  overrideExisting: false,
});

export const { usePricingListsQuery, usePricingListQuery } = pricingListsApi;

/**
 * A rate card changed — renamed, retired, or one of its rules edited.
 *
 * Both the card's own id and the `LIST` index, because the index carries the
 * rule and resource counts that a rule write moves. Nothing here invalidates
 * per-resource: that is the point of the model, and a screen showing a
 * PlayStation's rate is reading this card's entry rather than one of its own.
 */
export function invalidatePricingList(listId?: string) {
  return pricingListsApi.util.invalidateTags([
    { type: 'PricingList' as const, id: 'LIST' },
    ...(listId ? [{ type: 'PricingList' as const, id: listId }] : []),
  ]);
}

/**
 * A resource was attached to a card, or detached from one.
 *
 * `Resource` because the board's rows carry the card's name, and `PricingList`
 * because the index counts how many resources charge from each — the count is
 * what tells a manager the tenth console is on nothing, and it is wrong the
 * moment an attachment lands.
 */
export function invalidatePricingAttachment(resourceId?: string) {
  return pricingListsApi.util.invalidateTags([
    { type: 'PricingList' as const, id: 'LIST' },
    { type: 'Resource' as const, id: 'LIST' },
    ...(resourceId ? [{ type: 'Resource' as const, id: resourceId }] : []),
  ]);
}
